import React, { useState, useRef, useEffect, useContext } from "react";
import { Container, Form, Button, Card, Badge, Modal } from "react-bootstrap";
import {
  Headphones,
  Ticket,
  X,
  Send,
  Tag,
  Calendar,
  AlignLeft,
  MessageCircle,
  ChevronLeft,
  Clock,
} from "lucide-react";
import "../../../assets/css/Drivers/DriverSetting/DriverHelpCenter.css";
import DriverSettingsMasterLayout from "../../../layouts/Drivers/DriverSettingsMasterLayout";
import AppContext from "../../../context/AppContext";
import axios from "axios";

const DriverHelpCenter = () => {
  const { baseUrl, authToken } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState("create");
  const [ticketForm, setTicketForm] = useState({
    subject: "",
    category: "",
    description: "",
  });
  const [tickets, setTickets] = useState([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [modalView, setModalView] = useState("details");
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState({});
  const chatEndRef = useRef(null);

  const driverCategories = [
    "Payment Issue",
    "App Problem",
    "Ride Dispute",
    "Account Verification",
    "Vehicle Issue",
    "Coin Reward Problem",
    "Other",
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTicketForm((prev) => ({ ...prev, [name]: value }));
  };

  const fetchAllTickets = () => {
    const formData = new FormData();
    formData.append("tddriver_id",JSON.parse(localStorage.getItem("tddriver")).id,
    );
    axios
      .post(`${baseUrl}/driver/helpsupport/fetchAllTickets`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data.success === true) setTickets(res.data.tickets);
      })
      .catch((error) => console.log(error));
  };

  useEffect(() => {
    fetchAllTickets(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmitTicket = () => {
    if (
      !ticketForm.subject ||
      !ticketForm.category ||
      !ticketForm.description
    ) {
      alert("Please fill all fields");
      return;
    }
    const formData = new FormData();
    formData.append(
      "tddriver_id",
      JSON.parse(localStorage.getItem("tddriver")).id,
    );
    formData.append("subject", ticketForm.subject);
    formData.append("category", ticketForm.category);
    formData.append("description", ticketForm.description);
    formData.append("status", "Open");
    formData.append("raisedby", "driver");

    axios
      .post(`${baseUrl}/driver/helpsupport/createTicket`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        if (res.data.success === true) {
          alert("Ticket submitted successfully!");
          setTicketForm({ subject: "", category: "", description: "" });
          fetchAllTickets();
          setActiveTab("tickets");
        }
      })
      .catch(() => alert("Network Error!"));
  };

  const handleViewDetails = (ticket) => {
    setSelectedTicket(ticket);
    setModalView("details");
    setShowDetailModal(true);

    const formData = new FormData();
    formData.append("tdhelpsupport_id", ticket.id);
    axios
      .post(`${baseUrl}/driver/helpsupport/fetchConversation`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        console.log(res.data);
        if (res.data.success === true) {
          setConversationId(res.data.tdconversation?.id);
          const msgs =
            (res.data.tdconversation?.tdchats ?? []).map((chat) => ({
              id: chat.id,
              sender: chat.sender === "tddriver" ? "user" : "support",
              text: chat.message,
              time: new Date(chat.created_at).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              }),
            })) || [];
          setChatMessages((prev) => ({ ...prev, [ticket.id]: msgs }));
        }
      })
      .catch((error) => console.log(error));
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setModalView("details");
  };

  const handleSendMessage = () => {
  if (!chatInput.trim() || !selectedTicket || !conversationId) return;
    const formData = new FormData();
    formData.append("tdconversation_id", conversationId);
    formData.append("message", chatInput.trim());
    formData.append("type", "text");
    formData.append("Sender", "tddriver");

    axios
      .post(`${baseUrl}/driver/helpsupport/sendmessage`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data.success === true) {
          const newMsg = {
            id: Date.now(),
            sender: "user",
            text: chatInput.trim(),
            time: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          };
          setChatMessages((prev) => ({
            ...prev,
            [selectedTicket.id]: [...(prev[selectedTicket.id] || []), newMsg],
          }));
          setChatInput("");
        }
      })
      .catch(() => alert("Network Error!"));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  useEffect(() => {
    if (modalView === "chat") {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages, modalView]);

  const getStatusBadge = (status) => (
    <Badge className="DHC-status-badge">{status}</Badge>
  );

  const currentMessages = selectedTicket
    ? chatMessages[selectedTicket.id] || []
    : [];

  return (
    <DriverSettingsMasterLayout screenName={"DriverHelpCenter"} centered={true}>
      <div className="DriverHelpCenter-wrapper">
        <Container className="DriverHelpCenter-container">
          {/* Tabs */}
          <div className="DriverHelpCenter-tabs">
            <button
              className={`DriverHelpCenter-tab-btn ${activeTab === "create" ? "active" : ""}`}
              onClick={() => setActiveTab("create")}
            >
              <Headphones size={15} /> Create Ticket
            </button>
            <button
              className={`DriverHelpCenter-tab-btn ${activeTab === "tickets" ? "active" : ""}`}
              onClick={() => setActiveTab("tickets")}
            >
              <Ticket size={15} /> My Tickets ({tickets.length})
            </button>
          </div>

          {/* Create Ticket Form */}
          {activeTab === "create" && (
            <div className="DriverHelpCenter-form-section">
              <div className="DriverHelpCenter-create-header">
                <div className="DriverHelpCenter-icon-circle">
                  <Headphones size={28} color="#ffffff" />
                </div>
                <h2 className="DriverHelpCenter-create-title">
                  Submit a Ticket
                </h2>
                <p className="DriverHelpCenter-create-subtitle">
                  Describe your issue and we'll get back to you as soon as
                  possible
                </p>
              </div>

              <Form.Group className="DriverHelpCenter-form-group">
                <Form.Label className="DriverHelpCenter-field-label">
                  Subject
                </Form.Label>
                <Form.Control
                  type="text"
                  name="subject"
                  value={ticketForm.subject}
                  onChange={handleInputChange}
                  placeholder="Brief description of your issue"
                  className="DriverHelpCenter-input"
                />
              </Form.Group>

              <Form.Group className="DriverHelpCenter-form-group">
                <Form.Label className="DriverHelpCenter-field-label">
                  Category
                </Form.Label>
                <Form.Select
                  name="category"
                  value={ticketForm.category}
                  onChange={handleInputChange}
                  className="DriverHelpCenter-select"
                >
                  <option value="">Select a category</option>
                  {driverCategories.map((cat, index) => (
                    <option key={index} value={cat}>
                      {cat}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="DriverHelpCenter-form-group">
                <Form.Label className="DriverHelpCenter-field-label">
                  Description
                </Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  value={ticketForm.description}
                  onChange={handleInputChange}
                  placeholder="Please provide detailed information about your issue..."
                  className="DriverHelpCenter-textarea"
                />
              </Form.Group>

              <Button
                className="DriverHelpCenter-submit-btn"
                onClick={handleSubmitTicket}
              >
                Submit Ticket
              </Button>
            </div>
          )}

          {/* Tickets List */}
          {activeTab === "tickets" && (
            <div className="DriverHelpCenter-tickets-section">
              {tickets.length === 0 ? (
                <div className="DriverHelpCenter-empty">
                  <Ticket size={40} color="#1B9B82" />
                  <p className="DriverHelpCenter-empty-text">
                    No tickets found
                  </p>
                  <p className="DriverHelpCenter-empty-subtext">
                    Create a ticket to get help
                  </p>
                </div>
              ) : (
                <div className="DriverHelpCenter-tickets-list">
                  {tickets.map((ticket) => (
                    <Card
                      key={ticket.id}
                      className="DriverHelpCenter-ticket-card"
                    >
                      <Card.Body className="DriverHelpCenter-ticket-body">
                        <div className="DriverHelpCenter-ticket-card-top">
                          <div className="DriverHelpCenter-ticket-number">
                            <Ticket size={14} />
                            <span>
                              TKT-{String(ticket.id).padStart(3, "0")}
                            </span>
                          </div>
                          {getStatusBadge(ticket.status)}
                        </div>
                        <h6 className="DriverHelpCenter-ticket-subject">
                          {ticket.subject}
                        </h6>
                        <div className="DriverHelpCenter-ticket-meta">
                          <span>
                            <strong>Category:</strong> {ticket.category}
                          </span>
                          <span className="DriverHelpCenter-ticket-date">
                            <Clock size={12} />
                            {new Date(ticket.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="DriverHelpCenter-ticket-desc">
                          {ticket.description}
                        </p>
                        <div className="DriverHelpCenter-ticket-footer">
                          <Button
                            className="DriverHelpCenter-view-btn"
                            size="sm"
                            onClick={() => handleViewDetails(ticket)}
                          >
                            View Details
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </Container>
      </div>

      {/* Single Modal — Details + Chat */}
      {selectedTicket && (
        <Modal
          show={showDetailModal}
          centered
          onHide={handleCloseModal}
          className="DHC-detail-modal"
        >
          {/* Modal Header */}
          <div className="DHC-detail-modal-header">
            <div className="DHC-detail-modal-header-left">
              {modalView === "chat" && (
                <button
                  className="DHC-chat-modal-back"
                  onClick={() => setModalView("details")}
                >
                  <ChevronLeft size={16} />
                </button>
              )}
              <div className="DHC-detail-modal-header-icon">
                <Ticket size={20} color="#ffffff" />
              </div>
              <div className="DHC-detail-modal-header-text">
                <p>TKT-{String(selectedTicket.id).padStart(3, "0")}</p>
                <p>{selectedTicket.subject}</p>
              </div>
            </div>
            <button
              className="DHC-detail-modal-close"
              onClick={handleCloseModal}
            >
              <X size={16} />
            </button>
          </div>

          {/* Details View */}
          {modalView === "details" && (
            <>
              <Modal.Body className="DHC-detail-modal-body">
                <div className="DHC-detail-status-row">
                  <span className="DHC-detail-status-label">Status</span>
                  <Badge
                    className="DHC-status-badge"
                    style={{ backgroundColor: "#1B9B82" }}
                  >
                    {selectedTicket.status}
                  </Badge>
                </div>
                <div className="DHC-modal-divider" />

                <div className="DHC-detail-info-box">
                  <div className="DHC-detail-info-row">
                    <span className="DHC-detail-info-label">
                      <Tag size={13} /> CATEGORY
                    </span>
                    <span className="DHC-detail-info-value">
                      {selectedTicket.category}
                    </span>
                  </div>
                  <div className="DHC-detail-info-row">
                    <span className="DHC-detail-info-label">
                      <Calendar size={13} /> SUBMITTED ON
                    </span>
                    <span className="DHC-detail-info-value">
                      {new Date(selectedTicket.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                <div className="DHC-modal-divider" />
                <div className="DHC-detail-desc-box">
                  <p className="DHC-detail-desc-label">
                    <AlignLeft size={12} /> DESCRIPTION
                  </p>
                  <p className="DHC-detail-desc-text">
                    {selectedTicket.description}
                  </p>
                </div>

                <div
                  className={`DHC-status-notice DHC-status-notice--${selectedTicket.status?.toLowerCase().replace(" ", "-")}`}
                >
                  {selectedTicket.status === "Open" && (
                    <p>
                      ⏳ Your ticket is open and will be reviewed by our support
                      team shortly.
                    </p>
                  )}
                  {selectedTicket.status === "In Progress" && (
                    <p>🔧 Our team is actively working on your issue.</p>
                  )}
                  {selectedTicket.status === "Resolved" && (
                    <p>✅ This ticket has been resolved.</p>
                  )}
                </div>
              </Modal.Body>

              <div className="DHC-modal-footer">
                <Button
                  className="DHC-chat-support-btn"
                  onClick={() => setModalView("chat")}
                >
                  <MessageCircle size={16} style={{ marginRight: 8 }} /> Chat
                  with Support
                </Button>
                <Button className="DHC-close-btn" onClick={handleCloseModal}>
                  Close
                </Button>
              </div>
            </>
          )}

          {/* Chat View */}
          {modalView === "chat" && (
            <div className="DHC-chat-wrapper">
              <div className="DHC-chat-body">
                {currentMessages.length === 0 ? (
                  <div className="DHC-chat-empty">
                    <div className="DHC-chat-empty-icon">
                      <MessageCircle size={28} color="#1B9B82" />
                    </div>
                    <p className="DHC-chat-empty-title">No messages yet</p>
                    <p className="DHC-chat-empty-sub">
                      Send a message to start chatting with support
                    </p>
                  </div>
                ) : (
                  currentMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`DHC-msg-row ${msg.sender === "user" ? "DHC-msg-row--user" : "DHC-msg-row--support"}`}
                    >
                      {msg.sender === "support" && (
                        <div className="DHC-chat-avatar">S</div>
                      )}
                      <div
                        className={`DHC-bubble ${msg.sender === "user" ? "DHC-bubble--user" : "DHC-bubble--support"}`}
                      >
                        <p className="DHC-bubble-text">{msg.text}</p>
                        <span className="DHC-bubble-time">{msg.time}</span>
                      </div>
                    </div>
                  ))
                )}
                <div ref={chatEndRef} />
              </div>

              <div className="DHC-input-row">
                <textarea
                  className="DHC-chat-input"
                  placeholder="Type your message..."
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                />
                <button
                  className="DHC-send-btn"
                  onClick={handleSendMessage}
                  disabled={!chatInput.trim()}
                >
                  <Send size={16} color="#ffffff" />
                </button>
              </div>
            </div>
          )}
        </Modal>
      )}
    </DriverSettingsMasterLayout>
  );
};

export default DriverHelpCenter;
